import {
  Box,
  Button,
  Combobox,
  For,
  HStack,
  Highlight,
  Menu,
  Span,
  Spinner,
  Text,
  Textarea,
  VStack,
  useCombobox,
  useListCollection,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { t } from 'i18next';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiTarget } from 'react-icons/fi';
import { RiFormula } from 'react-icons/ri';

import { IndicatorCategory, IndicatorPublic, IndicatorsService } from '@/client';

import { DebouncedInput } from './DebouncedInput';

function getSearchIndicatorsQueryOptions({
  slug,
  search = '',
  id = '',
}: {
  slug: string;
  search: string;
  id?: string;
}) {
  return {
    queryKey: ['indicators', { search, id }],
    queryFn: async () => {
      return await IndicatorsService.readIndicators({
        offset: 0,
        limit: 5,
        companySlug: slug,
        search: search,
        filter: id && !search ? 'id=' + id : '',
      });
    },
    enabled: !!search || !!id,
  };
}

function getFormulaIndicatorsQueryOptions({ slug, indicatorIds = [] }: { slug: string; indicatorIds: string[] }) {
  return {
    queryKey: ['indicators', { indicatorIds }],
    queryFn: async () => {
      return await IndicatorsService.readIndicators({
        offset: 0,
        limit: 5,
        companySlug: slug,
        filter: 'id__in=' + indicatorIds.join(',') + (indicatorIds.length < 2 ? ',' : ''),
      });
    },
    enabled: !!indicatorIds,
  };
}

/* interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} */

function toUUID(str: string): string {
  if (!/^[0-9a-fA-F]{32}$/.test(str)) {
    throw new Error('Input must be a 32-character hex string');
  }
  return str.replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
}

const normalizeUuidsNoDashes = (str: string): string => {
  // UUID avec tirets : #123e4567-e89b-12d3-a456-426614174000
  const withDashesRegex = /#([0-9a-f]{8})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{12})/gi;

  return str.replace(withDashesRegex, (_, a, b, c, d, e) => `#${a}${b}${c}${d}${e}`);
};

export function CompositeIndicatorInput({
  value: _value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { slug } = useParams({ strict: false });
  const [value, setValue] = useState(normalizeUuidsNoDashes(_value) || '');
  const [searchIndicator, setSearchIndicator] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [suggestionStart, setSuggestionStart] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const { data: _suggestions } = useQuery(
    getSearchIndicatorsQueryOptions({
      slug: slug,
      search: searchIndicator,
    })
  );

  const { data: _formulaIndicators } = useQuery(
    getFormulaIndicatorsQueryOptions({
      slug: slug,
      indicatorIds: [...value.matchAll(/#([a-zA-Z0-9]{32})/g)].map((match) => toUUID(match[1])),
    })
  );

  const suggestions = useMemo(() => {
    return _suggestions?.data || [];
  }, [_suggestions]);

  const availableIndicators = useMemo(() => {
    return _formulaIndicators?.data || [];
  }, [_formulaIndicators]);

  // Validation des caractères autorisés
  const isValidCharacter = (char: string) => {
    const mathSymbols = /[+\-*\/()=<>≤≥≠√∑∏∫]/;
    const numbers = /[0-9.,]/;
    const spaces = /\s/;
    return mathSymbols.test(char) || numbers.test(char) || spaces.test(char);
  };

  /* 
  // Fonction de validation de la formule
  const validateFormula = (formula: string): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Si la formule est vide
    if (!formula.trim()) {
      return { isValid: true, errors: [], warnings: ['Formule vide'] };
    }

    // Vérification UUIDs
    const indicatorRegex = /#([a-zA-Z0-9]+)/g;
    let match;
    const usedIndicators = new Set<string>();

    while ((match = indicatorRegex.exec(formula)) !== null) {
      const uuid = match[1];
      const indicator = availableIndicators.find((ind) => ind.id?.replace(/-/g, '') === uuid);

      if (!indicator) {
        errors.push(`UUID d'indicateur inconnu: #${uuid}`);
      } else {
        usedIndicators.add(indicator.name);
      }
    }

    // Vérifier les opérateurs consécutifs
    const operatorRegex = /[+\-*\/]{2,}/g;
    if (operatorRegex.test(formula.replace(/\s/g, ''))) {
      errors.push('Opérateurs consécutifs détectés');
    }

    // Vérifier les opérateurs en début/fin
    const trimmed = formula.trim();
    if (trimmed && /[+\-*\/]$/.test(trimmed)) {
      errors.push('La formule ne peut pas se terminer par un opérateur');
    }
    if (trimmed && /^[*\/]/.test(trimmed)) {
      errors.push('La formule ne peut pas commencer par * ou /');
    }

    // Vérifier les nombres mal formés
    const numberRegex = /\d+\.\d*\./g;
    if (numberRegex.test(formula)) {
      errors.push('Nombres mal formés (plusieurs points décimaux)');
    }

    // Vérifier les parenthèses vides
    if (formula.includes('()')) {
      errors.push('Parenthèses vides détectées');
    }

    // Avertissements
    if (usedIndicators.size === 0 && formula.trim()) {
      warnings.push('Aucun indicateur utilisé dans la formule');
    }
    let result = 0;
    try {
      result = eval(formula.replace(indicatorRegex, '1'));
    } catch (error) {
      if (errors.length === 0) errors.push('Formule non calculable');
    } finally {
      if (isNaN(result) && errors.length === 0) errors.push('Formule non calculable');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };
 */
  // Validation en temps réel
  //const validation = useMemo(() => validateFormula(value), [value, availableIndicators]);

  // Synchroniser le scroll entre input et overlay
  const syncScroll = () => {
    if (inputRef.current && overlayRef.current) {
      overlayRef.current.scrollTop = inputRef.current.scrollTop;
      overlayRef.current.scrollLeft = inputRef.current.scrollLeft;
    }
  };

  // Gestion de la saisie
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDisplayValue = e.target.value;
    const newValue = displayToUuid(newDisplayValue, availableIndicators);
    const cursorPos = newValue.length || 0;
    // Validation des caractères
    if (newValue.length > value.length) {
      const lastChar = newDisplayValue[newDisplayValue.length - 1];
      // Permettre les lettres et underscores après @
      if (lastChar && !isValidCharacter(lastChar) && lastChar !== '@') {
        return;
      }
    }
    setValue(newValue);
    onChange(newValue);
    setCursorPosition(cursorPos);

    // Détecter si on est en train de taper un indicateur
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.trim().lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const searchTerm = textBeforeCursor.substring(lastAtIndex + 1);

      // Permettre les lettres, chiffres et underscores dans le nom d'indicateur
      if (/^[A-Z_0-9]*$/i.test(searchTerm)) {
        const filtered = availableIndicators.filter((indicator) =>
          indicator.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filtered.length > 0 || searchTerm.length === 0) {
          setShowSuggestions(true);
          setSuggestionStart(lastAtIndex);
          return;
        }
      }
    }

    setShowSuggestions(false);
  };

  // Gestion des touches clavier
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Gestion spéciale pour Delete et Backspace sur les indicateurs
    if (e.key === 'Delete' || e.key === 'Backspace') {
      const cursorPos = inputRef.current?.selectionStart || 0;
      const selectionEnd = inputRef.current?.selectionEnd || 0;

      if (cursorPos !== selectionEnd) {
        return;
      }

      const indicatorRegex = /@\[(.+?)\]/g;
      let match;
      const indicators = [];

      const _value = uuidToDisplay(value, availableIndicators);

      while ((match = indicatorRegex.exec(_value)) !== null) {
        indicators.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
        });
      }
      for (const indicator of indicators) {
        let shouldDelete = false;

        if (e.key === 'Delete') {
          shouldDelete = cursorPos >= indicator.start && cursorPos < indicator.end;
        } else if (e.key === 'Backspace') {
          shouldDelete = cursorPos > indicator.start && cursorPos <= indicator.end;
        }

        if (shouldDelete) {
          e.preventDefault();
          const textValue = uuidToDisplay(value, availableIndicators);
          const newValue = textValue.substring(0, indicator.start) + textValue.substring(indicator.end);
          setValue(displayToUuid(newValue, availableIndicators));

          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.setSelectionRange(indicator.start, indicator.start);
              inputRef.current.focus();
            }
          }, 0);
          return;
        }
      }
      return;
    }
    return;
  };

  // Insérer un indicateur
  const insertIndicator = (indicator: IndicatorPublic) => {
    const beforeSuggestion = value.substring(0, suggestionStart);
    const afterCursor = value.substring(cursorPosition);
    const uuidWithoutDashes = indicator.id?.replace(/-/g, '') || '';
    const newValue = beforeSuggestion + '#' + uuidWithoutDashes + afterCursor;
    const newCursorPos = suggestionStart + uuidWithoutDashes.length + 1;

    setValue(newValue);
    setShowSuggestions(false);
    setSearchIndicator('');

    setTimeout(() => {
      inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
      inputRef.current?.focus();
    }, 0);
  };

  // Cliquer sur une suggestion
  const handleSuggestionClick = (suggestion: IndicatorPublic) => {
    insertIndicator(suggestion);
  };

  // Mettre à jour la position du curseur
  const handleSelectionChange = () => {
    if (inputRef.current) {
      setCursorPosition(inputRef.current.selectionStart || 0);
    }
  };

  // Rendu avec coloration syntaxique
  const renderHighlightedText = (): React.ReactNode => {
    let result = '';
    let queryHighlight = [];
    let lastIndex = 0;

    const regex = /#([a-zA-Z0-9]+)/g;
    let match;

    const toUUID = (str: string): string => {
      return str.replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
    };

    while ((match = regex.exec(value)) !== null) {
      const matchStart = match.index;
      const matchEnd = regex.lastIndex;
      const rawId = match[1];

      // Append plain text between last match and current match
      result += value.substring(lastIndex, matchStart);

      let formattedId = rawId;
      if (/^[0-9a-fA-F]{32}$/.test(rawId)) {
        formattedId = toUUID(rawId);
      }

      const indicator = availableIndicators.find((ind) => ind.id === formattedId);

      result += `@${indicator ? `[${indicator.name}]` : rawId}`;
      queryHighlight.push(`@${indicator ? `[${indicator.name}]` : rawId}`);
      lastIndex = matchEnd;
    }

    // Append any remaining text after last match
    if (lastIndex < value.length) {
      result += value.substring(lastIndex);
    }

    return (
      <Highlight
        query={queryHighlight}
        styles={{
          rounded: 'md',
          bg: 'yellow.100',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal',
        }}
      >
        {result}
      </Highlight>
    );
  };

  const uuidToDisplay = (formula: string, indicators: IndicatorPublic[]) => {
    return formula.replace(/#([a-fA-F0-9]{32})/g, (_, uuid) => {
      const formatted = uuid.replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
      const indicator = indicators.find((ind) => ind.id === formatted);
      return indicator ? `@[${indicator.name}]` : `#${uuid}`;
    });
  };

  const displayToUuid = (display: string, indicators: IndicatorPublic[]) => {
    return display.replace(/@\[(.+?)\]/g, (_, name) => {
      const indicator = indicators.find((ind) => ind.name === name);
      const uuid = indicator?.id?.replace(/-/g, '');
      return uuid ? `#${uuid}` : `@[${name}]`; // conserve le nom si non résolu
    });
  };

  useEffect(() => {
    setValue(normalizeUuidsNoDashes(_value) || '');
  }, [_value]);

  return (
    <Box w={'full'}>
      <Box pos="relative">
        <Box pos="relative">
          {/* Overlay pour la coloration syntaxique */}
          <Box
            ref={overlayRef}
            pos={'absolute'}
            overflow={'hidden'}
            whiteSpace={'pre-wrap'}
            wordBreak={'break-all'}
            pointerEvents={'none'}
            border={'1px solid transparent'}
            inset={0}
            p={3}
            onScroll={syncScroll}
          >
            {renderHighlightedText()}
          </Box>

          {/* Textarea invisible pour la saisie */}
          <Textarea
            autoFocus
            autoresize
            placeholder={t(`indicator.add.formula.placeholder`)}
            p={3}
            ref={inputRef}
            value={uuidToDisplay(value, availableIndicators)}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSelect={handleSelectionChange}
            onScroll={syncScroll}
            spellCheck={false}
          />
        </Box>
        {/* Liste des suggestions */}
        {showSuggestions && (
          <Menu.Root
            open={showSuggestions}
            positioning={{
              getAnchorRect: () => inputRef.current!.getBoundingClientRect(),
            }}
          >
            <Menu.Positioner>
              <Menu.Content maxW={'md'}>
                <DebouncedInput
                  placeholder="Rechercher un indicateur"
                  value={searchIndicator}
                  onChange={(value) => setSearchIndicator(value as string)}
                ></DebouncedInput>
                {suggestions.length > 0 &&
                  suggestions.map((suggestion) => (
                    <Menu.Item
                      key={suggestion.id}
                      onClick={() => handleSuggestionClick(suggestion)}
                      value={suggestion.id ?? ''}
                    >
                      <VStack placeItems={'start'} gap={0}>
                        <Text fontWeight={'semibold'}>{suggestion.name}</Text>
                        <Text fontSize={'sm'} color={'gray.500'}>
                          {t('indicator.columnsHeader.categoryName')}:{' '}
                          {t(`indicator.columnsHeader.category.${suggestion.category}`)}
                          {' - '}
                          {t('indicator.columnsHeader.unit')}: {suggestion.unit ?? 'N/R'}
                        </Text>
                      </VStack>
                    </Menu.Item>
                  ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}
        <HStack padding={1} bg={'#FFAE00'} w={'fit-content'} borderRadius={'3px'} color={'#175C5C'}>
          <RiFormula /> {t('indicator.formulaDialog.hint')}
        </HStack>
      </Box>

      {/* Erreurs et avertissements */}
      {/* {(validation.errors.length > 0 || validation.warnings.length > 0) && (
        <VStack placeItems={'start'} pt={2}>
          {validation.errors.length > 0 && (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Erreurs :</Alert.Title>
                <Alert.Description>
                  <Box as="ul" listStyleType="circle">
                    {validation.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </Box>
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}

          {validation.warnings.length > 0 && (
            <Alert.Root status="warning">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Avertissements :</Alert.Title>
                <Alert.Description>
                  <Box as="ul" listStyleType="circle">
                    {validation.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </Box>
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}
        </VStack>
      )} */}

      {/* Aperçu avec les noms */}
      {/*<VStack placeItems={"start"}>
                <Text fontWeight={"semibold"}>Aperçu avec noms :</Text>
                <Text>{renderHighlightedText() || "Aucune formule saisie"}</Text>
            </VStack> */}

      {/* Aperçu avec les UUIDs */}
      {/*<VStack placeItems={"start"}>
                <Text fontWeight={"semibold"}>Aperçu avec UUIDs :</Text>
                <Text>{value || "Aucune formule saisie"}</Text>
            </VStack>*/}
    </Box>
  );
}

export function SimpleIndicatorInput({
  value: _value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const { slug } = useParams({ strict: false });
  const [inputValue, setInputValue] = useState('');

  const {
    data: _suggestions,
    isLoading,
    isError,
    error,
  } = useQuery({
    ...getSearchIndicatorsQueryOptions({
      slug: slug,
      search: inputValue,
      id: _value ?? '',
    }),
  });

  const { collection, set } = useListCollection<IndicatorPublic>({
    initialItems: [],
    itemToString: (item) => item.name,
    itemToValue: (item) => item.id ?? '',
  });

  const combobox = useCombobox({
    collection,
    defaultValue: _value ? [_value] : [],
    placeholder: 'Example: Dexter',
    inputValue,
    onInputValueChange: (e) => setInputValue(e.inputValue),
    onValueChange: (e) => {
      onChange(e.value.length ? e.value[0] : null);
      setInputValue('');
    },
  });

  useEffect(() => {
    if (_suggestions?.data) {
      set(_suggestions.data);
    }
  }, [_suggestions, set]);

  // Rehydrate the value
  const hydrated = useRef(false);

  useEffect(() => {
    if (combobox.value.length && collection.size && !hydrated.current) {
      combobox.syncSelectedItems();
      hydrated.current = true;
    }
  }, [combobox.value, collection.size]);

  return (
    <Combobox.RootProvider value={combobox} width="full">
      <Combobox.Control>
        <Combobox.Input />
        <Combobox.IndicatorGroup>
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>

      <Combobox.Positioner>
        <Combobox.Content>
          {isLoading ? (
            <HStack p="2">
              <Spinner size="xs" />
              <Span>Chargement...</Span>
            </HStack>
          ) : isError ? (
            <Span p="2" color="fg.error">
              {error.message}
            </Span>
          ) : (
            <For each={collection.items} fallback={<Combobox.Empty>Aucun indicateur trouvé</Combobox.Empty>}>
              {(item) => (
                <Combobox.Item key={item.name} item={item}>
                  <VStack placeItems={'start'} gap={0}>
                    <Text fontWeight={'semibold'}>{item.name}</Text>
                    <Text fontSize={'sm'} color={'gray.500'}>
                      {t('indicator.columnsHeader.categoryName')}:{' '}
                      {t(`indicator.columnsHeader.category.${item.category}`)}
                      {' - '}
                      {t('indicator.columnsHeader.unit')}: {item.unit ?? 'N/R'}
                    </Text>
                  </VStack>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              )}
            </For>
          )}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.RootProvider>
  );
}

export function IndicatorInput({
  value,
  onChange,
}: {
  value: IndicatorCategory | undefined;
  onChange: (value: IndicatorCategory) => void;
}) {
  const { t } = useTranslation();

  const getButtonStyles = (isSelected: boolean) => {
    return {
      bg: isSelected ? '#A0E7E7' : '#F4F6F5',
      color: isSelected ? '#175C5C' : '#565F5C',
      _hover: {
        bg: isSelected ? '#A0E7E7' : '#E8ECEB',
      },
    };
  };

  return (
    <HStack>
      <Button {...getButtonStyles(value === 'simple')} onClick={() => onChange('simple')}>
        <FiTarget />
        {t('indicator.columnsHeader.category.simple')}
      </Button>

      <Button {...getButtonStyles(value === 'composite')} onClick={() => onChange('composite')}>
        <RiFormula />
        {t('indicator.columnsHeader.category.composite')}
      </Button>

      <Button {...getButtonStyles(value === 'qualitative')} onClick={() => onChange('qualitative')}>
        <FiTarget />
        {t('indicator.columnsHeader.category.qualitative')}
      </Button>
    </HStack>
  );
}
